import { API_V2_ROOT } from "@/config";

// The /docs-auth/* endpoints ARE the docs visitor-authentication system, so
// they are pre-authentication and take no signed-in Freshpaint user. They are
// therefore exempt from apiV2AuthHeaders() (the same exemption the OIDC login
// callback has): the Firebase path passes its ID token in the request body,
// and the access-code path passes only the code. There is no locker env or
// masquerade context to forward.

export interface DocsLoginResult {
  /** Absolute GitBook URL (including the signed jwt_token) to navigate to. */
  redirectUrl: string;
}

/**
 * Result of a Firebase docs login. The visitor is either redirected into the
 * docs, or must first accept the documentation terms of use (the login page
 * then shows the agreement and re-submits with `agreeTerms`).
 */
export type DocsFirebaseLoginResult =
  | { status: "redirect"; redirectUrl: string }
  | { status: "terms-required" };

async function postDocsAuth(
  path: string,
  body: Record<string, string | boolean>,
): Promise<Response> {
  return fetch(`${API_V2_ROOT}/docs-auth/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

async function parseRedirect(resp: Response): Promise<DocsLoginResult> {
  const data = (await resp.json()) as { redirect_url?: string };
  if (!data.redirect_url) {
    throw new Error("Something went wrong. Please try again.");
  }
  return { redirectUrl: data.redirect_url };
}

/**
 * Exchanges a temporary access code for a signed docs redirect URL.
 * `location` is the page the visitor originally requested (forwarded by
 * GitBook) so they can be returned to it after sign-in.
 */
export async function loginWithAccessCode(
  code: string,
  location: string,
): Promise<DocsLoginResult> {
  const resp = await postDocsAuth("access-code", { code, location });
  if (resp.status === 401) {
    throw new Error("That access code is invalid or has expired.");
  }
  if (!resp.ok) {
    throw new Error("Something went wrong. Please try again.");
  }
  return parseRedirect(resp);
}

/**
 * Exchanges a Firebase ID token (core identity) for a signed docs redirect URL.
 * When the visitor has never accepted the documentation terms of use, the
 * backend responds with `{terms_required: true}` instead of a redirect; pass
 * `agreeTerms` to record acceptance and complete sign-in.
 */
export async function loginWithFirebaseToken(
  idToken: string,
  location: string,
  agreeTerms = false,
): Promise<DocsFirebaseLoginResult> {
  const resp = await postDocsAuth("firebase", {
    id_token: idToken,
    location,
    ...(agreeTerms ? { agree_terms: true } : {}),
  });
  if (resp.status === 401) {
    throw new Error("Your session has expired. Please sign in again.");
  }
  if (resp.status === 403) {
    throw new Error("Your account doesn't have access to these docs.");
  }
  if (!resp.ok) {
    throw new Error("Something went wrong. Please try again.");
  }

  const data = (await resp.json()) as {
    redirect_url?: string;
    terms_required?: boolean;
  };
  if (data.terms_required) {
    return { status: "terms-required" };
  }
  if (!data.redirect_url) {
    throw new Error("Something went wrong. Please try again.");
  }
  return { status: "redirect", redirectUrl: data.redirect_url };
}
